import { useSyncExternalStore } from 'react';

import type { PostHogFeatureFlag } from '@/config/availableFeatureFlags';

/**
 * The flags a page behind a flag gate is holding open (see useFeatureFlagGate). Links to that
 * page, the sidebar item among them, keep showing while it is open, so another tab's sign-out
 * that turns the flag off here does not take away the nav item of the page the user is on.
 * Counted, since two mounts can hold one flag.
 */
const holds = new Map<PostHogFeatureFlag, number>();
const listeners = new Set<() => void>();
let snapshot: ReadonlySet<PostHogFeatureFlag> = new Set();

function publish(): void {
    snapshot = new Set(holds.keys());
    for (const listener of [...listeners]) listener();
}

/** Holds `flag` open until the returned release runs; each release counts once. */
export function holdFlagOpen(flag: PostHogFeatureFlag): () => void {
    holds.set(flag, (holds.get(flag) ?? 0) + 1);
    publish();
    let released = false;
    return () => {
        if (released) return;
        released = true;
        const left = (holds.get(flag) ?? 1) - 1;
        if (left > 0) holds.set(flag, left);
        else holds.delete(flag);
        publish();
    };
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot(): ReadonlySet<PostHogFeatureFlag> {
    return snapshot;
}

export function useHeldOpenFlags(): ReadonlySet<PostHogFeatureFlag> {
    return useSyncExternalStore(subscribe, getSnapshot);
}

export function useFlagHeldOpen(flag: PostHogFeatureFlag): boolean {
    return useHeldOpenFlags().has(flag);
}

/** Whether `key` is one of the flags held open; a string key, as the sidebar config names flags. */
export function isHeldOpen(held: ReadonlySet<PostHogFeatureFlag>, key: string): boolean {
    return [...held].some((flag) => flag === key);
}
